/**
 * BTM coverage auto-fill — infers technology hints from SED solar/wind
 * resource sections for the `btm-vendor-quote` requirement.
 */
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { RequirementAutoFill } from './compileCoverageAutoFill';

export const BTM_VENDOR_QUOTE_FIELD_KEYS = [
  'technology',
  'capacity_MW',
  'lcoe_USDperMWh',
  'leadTime_months',
  'vendorName',
] as const;

const SOLAR_GHI_STRONG = 4.5;
const WIND_CF_STRONG = 0.30;

export function mapBtmSedToCoverage(
  sed: SiteEvidenceDocument,
): Pick<RequirementAutoFill, 'fields' | 'notes' | 'confidence' | 'fieldsFilledCount' | 'fieldsTotalCount'> {
  const fields: Record<string, string | number> = {};
  const hints: string[] = [];

  const solar = sed.solar?.data;
  if (solar && typeof solar.ghi === 'number' && solar.ghi >= SOLAR_GHI_STRONG) {
    fields.technology = 'solar';
    hints.push(`GHI ${solar.ghi.toFixed(1)} kWh/m²/day`);
  }

  const wind = sed.wind?.data;
  if (
    wind
    && typeof wind.capacityFactor === 'number'
    && wind.capacityFactor >= WIND_CF_STRONG
    && !fields.technology
  ) {
    fields.technology = 'wind';
    hints.push(`wind CF ${(wind.capacityFactor * 100).toFixed(0)}%`);
  }

  const gridCap = sed.grid?.data?.capacity;
  if (typeof gridCap === 'string' && gridCap.toLowerCase().includes('high') && !fields.technology) {
    hints.push('grid capacity high — compare BTM vs grid path');
  }

  return {
    fields,
    notes: hints.length > 0
      ? `SED resource hint: ${hints.join('; ')}. Vendor quote still required for credible BTM economics.`
      : 'No strong BTM technology signal on SED — capture vendor quote or study.',
    confidence: {
      tier: fields.technology ? 'low' : 'low',
      value: 0.45,
      rationale: 'SED resource inference only — not a vendor quote',
    },
    fieldsFilledCount: Object.keys(fields).length,
    fieldsTotalCount: BTM_VENDOR_QUOTE_FIELD_KEYS.length,
  };
}

export function btmSedToRequirementAutoFill(sed: SiteEvidenceDocument): RequirementAutoFill | null {
  const mapped = mapBtmSedToCoverage(sed);
  if (mapped.fieldsFilledCount === 0) return null;
  return {
    requirementId: 'btm-vendor-quote',
    ...mapped,
    sourceProvider: 'sed-btm-inference',
    sourceUrl: '',
  };
}
